import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATTRAPPE, hole, sende } from "./klient";
import { zahlLesen } from "./zahlfeld";
import type { Fotoeintrag } from "./fotos";
import * as A from "./attrappe/aufnahmen";
import type { Aufnahmetermin } from "./typen";
import { tagKurz as tagKurzImBetrieb, tageBis } from "./betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Die Ortsbegehung am Telefon und das Nachfassen daneben (0112).

   **Eine eigene Datei**, wie die Einsatzzeit eine hat. Beide Listen
   gehören demselben Menschen — dem Außendienst kaufmännisch —, beide
   werden zwischen zwei Terminen gelesen, und beide haben ihre eigenen
   Wege (`/aufnahmen`, `/nachfassen`). Was sie brauchen, steht beisammen;
   angeglichen wird trotzdem hier und nur hier: In den Masken steht kein
   Feldname aus der Datenbank.

   **Der Pool liegt schon in `haken.ts`** (`useAufnahmen`, `useAufnahme…`)
   — dieselben Wege, die der große Schirm geht. Hier stehen die beiden
   Dinge, die es dort nicht gibt: **eine einzelne** Aufnahme (der Schirm
   vor Ort holt nicht zweihundert Zeilen, um eine zu zeigen) und ein
   Ergebnis mit dem, was auf der Wiese dazugehört — Fotos, der Mensch am
   Tor, Zufahrt, Untergrund, Hindernisse.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const alsListe = (wert: unknown): Roh[] => (Array.isArray(wert) ? (wert as Roh[]) : []);
const txt = (wert: unknown): string =>
  wert === null || wert === undefined ? "" : typeof wert === "string" ? wert : String(wert);
const zahlOderNull = (wert: unknown): number | null => {
  if (wert === null || wert === undefined || wert === "") return null;
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : null;
};

/* ══════════════ Die Begehung ══════════════ */

/**
 * Eine Ortsaufnahme, wie der Schirm vor Ort sie braucht.
 *
 * `menge`, `notiz` und die Felder des Ortes stehen auch dann da, wenn sie
 * leer sind: Beim zweiten Durchgang derselben Anlage ist die Zufahrt von
 * letztem Mal die Vorbelegung, und genau das ist Regel `[4]` aus
 * 19-ortsaufnahme.md — sonst fährt jemand hin und schreibt sie neu.
 */
/** Eine Zeile des Angebots, so wie sie vor Ort vorgelesen wird. */
export type Preiszeile = {
  id: string;
  bezeichnung: string;
  menge: number;
  einheit: string;
  einzelpreis: number;
  netto: number;
  art: string;
  /** Wahl- und Alternativpositionen zählen nicht in die Summe. */
  zaehlt: boolean;
};

export type Angebotspreis = {
  zeilen: Preiszeile[];
  /** Was die Zuschläge dazu ausmachen — gerechnet vom Server. */
  zuschlaegeNetto: number;
  nettoPositionen: number;
  /** Die Zahl, die man dem Kunden nennt. */
  netto: number;
  /** Mindestens eine Zuschlagszeile wartet noch auf eine Messung. */
  offen: boolean;
  /**
   * **Die Zuschlagszeilen einzeln** (Paket aufnahmeakte, 08.10.2026) — die
   * Zeilen der Zuschlagserwartung, wie der Server sie gerechnet hat. Gelesen,
   * nicht gerechnet: Die Akte zeigt sie unter der Position, an der sie hängen.
   */
  zuschlagzeilen?: Preiszuschlagzeile[];
};

/** Eine Zeile der Zuschlagserwartung (`dienste/zuschlagserwartung.ts`, `Erwartungszeile`). */
export type Preiszuschlagzeile = {
  code: string;
  bezeichnung: string;
  /** `null`: noch kein Betrag (Menge folgt, Preis fehlt). */
  netto: number | null;
  menge: number | null;
  einzelpreis: number;
  /** i18n-Schlüssel der Einheit (`einheit.qm`). */
  einheitKey: string;
  offen: "" | "menge_folgt" | "betrag_fehlt" | "preis_fehlt";
  /** Die Position, an der sie hängt — leer, wenn sie für den ganzen Vorgang gilt. */
  positionId: string;
  jeAuftrag: boolean;
};

export type Begehung = {
  id: string;
  /** `offen` · `erledigt` · `verworfen`. */
  status: string;
  kunde: string;
  kundennummer: string;
  /** Der Einsatzort. Leer heißt: Bei diesem Kunden gibt es ihn noch nicht. */
  ort: string;
  ortschaft: string;
  ortId: string;
  vorgang: string;
  /** Die Angebotsnummer des Vorgangs (AG-…), wenn es eine gibt (Paket aufnahmeakte). */
  angebotNummer?: string;
  betreff: string;
  position: string;
  /** Die Position, auf die sie wartet bzw. in die die Zahl ging (`item_id`). */
  positionId?: string;
  /** Was gemessen wird — `QM`, `MODUL`. Die Einheit ist das Wort dafür. */
  metric: string;
  einheit: string;
  /** Wer sie genommen hat. Leer heißt: im Pool. */
  wer: string;
  werId: string;
  /** Wann sie bestellt wurde — **kein Termin**. */
  bestelltIso: string;
  /** Der feste Termin, wenn einer ausgemacht ist (Migration 0180). Sonst `null`. */
  termin: Aufnahmetermin | null;
  erledigtIso: string;
  /** Der Wunschtermin ohne Person (0285). */
  wunsch?: { von: string; bis: string; schaerfe: "genau" | "kw" } | null;
  /** Wofür gemessen wird, als Wort (Paket aufnahmen2). */
  leistung?: string;
  /** Zustand des Vorgangs, an dem sie hängt — leer ohne Vorgang. */
  vorgangStatus?: string;
  menge: number | null;
  notiz: string;
  /* Die Bilder stehen nicht hier. Sie sind Zeilen in `photo` und werden über
     `GET /fotos?aufnahme=` gelesen (Welle 2, 18.09.2026). */
  ansprechpartner: string;
  zufahrt: string;
  untergrund: string;
  hindernisse: string;
  /**
   * **Wo das Gerät stehen kann** (Migration 0156). Luca, 21.09.2026: *„Vor
   * allem muss ich auch erfassen können, wo der Kran stehen kann, also
   * Informationen zu dem Platz."*
   *
   * Steht am **Ort** wie Zufahrt und Untergrund — beim zweiten Durchgang
   * derselben Anlage ist der Platz derselbe.
   */
  stellplatz: string;
  /* Hier standen bis zum 21.09.2026 `faktoren` und `faktorenGewaehlt` — die
     Preistreiber der Leistungsart als Ankreuzfelder. Die Gattung ist abgebaut
     (Migration 0164): Verschmutzungsgrad und Anlagenart sind Preiskategorien,
     Wasserbereitstellung und Flechten sind Leistungszuschläge. */
  /**
   * **Die Preiskategorien dieser Leistungsart** (G9, Migration 0142) — der
   * Verschmutzungsgrad.
   *
   * **Eine Auswahl, kein Haken:** leicht ODER mittel ODER stark, und jeder
   * Grad trägt seinen eigenen Satz je Einheit statt eines Aufschlags auf einen
   * Grundsatz.
   */
  kategorien: Preiskategoriezeile[];
  /** Welcher Grad schon festgestellt ist. Leer heißt: noch keiner. */
  kategorieGewaehlt: string;
  /**
   * Der **Vorgabegrad** der Leistungsart (Migration 0184) — gilt, solange
   * keiner festgestellt ist, und dann als Annahme. Leer: keine Vorgabe.
   */
  kategorieVorgabe?: string;
  /**
   * **Die Leistungszuschläge dieser Leistungsart** (Migration 0152) — der
   * Zusatz, der zum Grad dazukommt: Flechten.
   *
   * Etwas anderes als der Grad daneben: **ein Haken.** Der Grad gilt einmal,
   * ein Zuschlag kommt obendrauf, so viele, wie angekreuzt sind. Sein Satz
   * hängt am Grad, deshalb trägt er alle Sätze mit und die Maske liest den
   * passenden erst aus, wenn der Grad feststeht.
   */
  zuschlaege: Leistungszuschlagzeile[];
  /** Was vor Ort schon angekreuzt ist. */
  zuschlaegeGewaehlt: string[];
  /**
   * **Für welche Fläche** (Migration 0153) — 500 von 1.500 m². `null` oder
   * fehlend heißt „die ganze": So standen die Haken bis zum 19.09.2026.
   */
  zuschlaegeFlaechen?: Record<string, number | null>;
  /**
   * **Die Leistungsart der wartenden Position** (Migration 0154) — sie
   * beantwortet die Frage nach Gerät und Mensch (`GET /leistungslage`).
   * Leer, wenn die Aufnahme an keiner Position hängt.
   */
  leistungsartId: string;
  /**
   * **Wann die Leistung stattfinden soll** — im Angebot eine Kalenderwoche
   * (`zeitschaerfe: "kw"`, Montag und Sonntag), vor Ort ein Tag.
   *
   * Luca, 21.09.2026: *„Bei der Ortsaufnahme sagt man: Das kann ein genaues
   * Datum sein, damit man das auch testen kann, wenn das klappt."* Die Woche
   * ist damit der **Rahmen**, in dem der Vertriebler den Tag wählt — und
   * nicht eine zweite Angabe daneben.
   */
  ausfuehrungVon: string;
  ausfuehrungBis: string;
  zeitschaerfe: "genau" | "kw";
  /**
   * **Was das Angebot kostet, so wie es jetzt dasteht** (Luca, 21.09.2026:
   * *„…dann kann man dem Kunden direkt die Auskunft geben, die er
   * braucht"*). `null`, wenn die Aufnahme an keinem Angebot hängt.
   *
   * `offen` heißt: Mindestens ein Zuschlag wartet noch auf eine Messung —
   * die Summe ist dann ein Teil und keine Zusage, und der Schirm sagt es.
   */
  preis: Angebotspreis | null;
};

/**
 * Was vor Ort für einen Zuschlag eingetragen wird — **Anteil oder Fläche**.
 *
 * Luca, 19.09.2026: *„Dann einfach Balken 0 25 50 75 100 Prozent mit Flechten
 * ODER alternativ einen Quadratmeterwert, der nicht höher sein kann als die
 * Gesamtfläche."*
 *
 * Zwei Wege zu derselben Zahl, und welcher gewählt wurde, bleibt stehen: Wer
 * „50 %" drückt und danach die Menge von 1.500 auf 1.600 korrigiert, meint
 * 800 m² und nicht weiterhin 750. Wer „500 m²" tippt, meint 500.
 */
export type Zuschlagsanteil =
  | { art: "anteil"; prozent: 0 | 25 | 50 | 75 | 100 }
  | { art: "flaeche"; text: string };

/** Die Balken — nicht mehr. Eine feinere Stufe tippt man als Fläche. */
export const ANTEILE = [0, 25, 50, 75, 100] as const;

/**
 * Die Fläche eines Eintrags bei dieser Gesamtfläche — und was daran nicht
 * stimmt.
 *
 * * `menge: 0` — kein Zuschlag (0 % oder ein leeres Feld).
 * * `menge: null` ohne Fehler — ein Anteil, solange die Gesamtfläche fehlt:
 *   „50 %" von nichts ist noch keine Zahl, aber auch kein Fehler.
 * * `fehler: "zuGross"` — mehr als die ganze Fläche. **Nicht still gekappt:**
 *   2.000 m² Flechten auf 1.500 m² sind ein Vertipper, und die Maske sagt es.
 * * `fehler: "unlesbar"` — keine Zahl.
 */
export function zuschlagsflaecheAus(
  eintrag: Zuschlagsanteil,
  gesamt: number | null,
): { menge: number | null; fehler: null | "zuGross" | "unlesbar" } {
  const hatGesamt = gesamt !== null && gesamt > 0;
  if (eintrag.art === "anteil") {
    if (eintrag.prozent === 0) return { menge: 0, fehler: null };
    return { menge: hatGesamt ? Math.round(gesamt * eintrag.prozent) / 100 : null, fehler: null };
  }
  if (eintrag.text.trim() === "") return { menge: 0, fehler: null };
  // Dieselbe Lesart wie das Mengenfeld darüber: „1.200" ist tausendzweihundert.
  const zahl = zahlLesen(eintrag.text);
  if (zahl === null || zahl < 0) return { menge: null, fehler: "unlesbar" };
  if (hatGesamt && zahl > gesamt) return { menge: null, fehler: "zuGross" };
  return { menge: zahl, fehler: null };
}

/** Eine Zeile aus `service_surcharge`, wie der Schirm vor Ort sie braucht. */
export type Leistungszuschlagzeile = {
  id: string;
  code: string;
  bezeichnung: string;
  /** Der Betrag ist erfunden und mit niemandem abgestimmt (Migration 0036). */
  platzhalter: boolean;
  /** Der Satz je Einheit, je Grad. `kategorieId` leer heißt „für jeden Grad". */
  saetze: { kategorieId: string; netto: number }[];
};

/**
 * **Welcher Satz gilt für diesen Grad?** — dieselbe Regel wie in `fach`
 * (`satzFuerKategorie`, Migration 0152): Die eigene Zeile schlägt die
 * allgemeine.
 *
 * `null` heißt: keine von beiden ist gepflegt. Dann wirkt der Zuschlag hier
 * nicht, und die Maske sagt es — statt ihn wie einen kostenlosen zu zeigen.
 */
export function zuschlagssatz(
  z: Leistungszuschlagzeile,
  kategorieId: string,
): number | null {
  const eigen = kategorieId ? z.saetze.find((r) => r.kategorieId === kategorieId) : undefined;
  if (eigen) return eigen.netto;
  return z.saetze.find((r) => r.kategorieId === "")?.netto ?? null;
}

/** Eine Zeile aus `service_type_variant`, wie der Schirm vor Ort sie braucht. */
export type Preiskategoriezeile = {
  id: string;
  code: string;
  bezeichnung: string;
  /**
   * Wie viele Sätze je Einheit für diesen Grad gepflegt sind.
   *
   * `0` heißt: Der Grad steht im Katalog, sein Preis nicht. Die Maske zeigt
   * das an, statt ihn wie einen gepflegten aussehen zu lassen — sonst wählt
   * jemand den Grad und bekommt schweigend keinen Preis.
   */
  saetze: number;
};

function alsBegehung(z: Roh): Begehung {
  return {
    id: txt(z["id"]),
    status: txt(z["status"]) || "offen",
    kunde: txt(z["kunde"]),
    kundennummer: txt(z["kundennummer"]),
    ort: txt(z["ort"]),
    ortschaft: txt(z["ortschaft"]),
    ortId: txt(z["site_id"]),
    vorgang: txt(z["vorgang"]),
    angebotNummer: txt(z["angebot_nummer"]),
    betreff: txt(z["betreff"]),
    position: txt(z["position"]),
    positionId: txt(z["item_id"]),
    metric: txt(z["metric"]),
    einheit: txt(z["einheit"]),
    wer: txt(z["wer"]),
    werId: txt(z["assigned_to"]),
    bestelltIso: txt(z["created_at"]),
    termin: txt(z["termin_von"]) && txt(z["termin_bis"])
      ? {
        von: txt(z["termin_von"]), bis: txt(z["termin_bis"]),
        person: txt(z["termin_person"]), personId: txt(z["termin_person_id"]),
      }
      : null,
    erledigtIso: txt(z["done_at"]),
    /* Der Wunsch ohne Person (0285) — für „In Einteilen planen" (Paket aufnahmen2). */
    wunsch: (() => {
      const von = txt(z["wunsch_von"]); const bis = txt(z["wunsch_bis"]); const s = txt(z["wunsch_schaerfe"]);
      return von && bis && (s === "genau" || s === "kw") ? { von, bis, schaerfe: s } : null;
    })(),
    /* Die Leistung als Wort, für „Angebot daraus erstellen" (Paket aufnahmen2). */
    leistung: txt(z["leistung"]),
    vorgangStatus: txt(z["vorgang_status"]),
    menge: zahlOderNull(z["quantity"]),
    notiz: txt(z["note"]),
    ansprechpartner: txt(z["ansprechpartner"]),
    zufahrt: txt(z["zufahrt"]),
    untergrund: txt(z["untergrund"]),
    hindernisse: txt(z["hindernisse"]),
    stellplatz: txt(z["stellplatz"]),
    kategorien: alsListe(z["kategorien"]).map((k): Preiskategoriezeile => ({
      id: txt(k["id"]),
      code: txt(k["code"]),
      bezeichnung: txt(k["label"]) || txt(k["code"]),
      saetze: zahlOderNull(k["saetze"]) ?? 0,
    })),
    kategorieGewaehlt: txt(z["kategorie_gewaehlt"]),
    kategorieVorgabe: txt(z["kategorie_vorgabe"]),
    zuschlaege: alsListe(z["zuschlaege"]).map((x): Leistungszuschlagzeile => ({
      id: txt(x["id"]),
      code: txt(x["code"]),
      bezeichnung: txt(x["label"]) || txt(x["code"]),
      platzhalter: x["platzhalter"] === true,
      saetze: alsListe(x["saetze"]).map((r) => ({
        kategorieId: txt(r["variant_id"]),
        netto: zahlOderNull(r["surcharge_net"]) ?? 0,
      })),
    })),
    zuschlaegeGewaehlt: (Array.isArray(z["zuschlaege_gewaehlt"]) ? z["zuschlaege_gewaehlt"] : [])
      .map((x) => txt(x))
      .filter(Boolean),
    zuschlaegeFlaechen: Object.fromEntries(
      alsListe(z["zuschlaege_flaechen"])
        .filter((f) => txt(f["id"]))
        .map((f) => [txt(f["id"]), zahlOderNull(f["menge"])]),
    ),
    leistungsartId: txt(z["leistungsart_id"]),
    ausfuehrungVon: txt(z["ausfuehrung_von"]),
    ausfuehrungBis: txt(z["ausfuehrung_bis"]),
    zeitschaerfe: txt(z["zeitschaerfe"]) === "kw" ? "kw" : "genau",
    preis: alsPreis(z["preis"]),
  };
}

/** Die Preisauskunft der Aufnahme — `null`, wo kein Angebot dahintersteht. */
function alsPreis(wert: unknown): Angebotspreis | null {
  if (typeof wert !== "object" || wert === null || Array.isArray(wert)) return null;
  const p = wert as Roh;
  const zu = (typeof p["zuschlaege"] === "object" && p["zuschlaege"] !== null
    ? p["zuschlaege"] as Roh : {});
  return {
    zeilen: alsListe(p["positionen"]).map((z): Preiszeile => ({
      id: txt(z["id"]),
      bezeichnung: txt(z["label"]),
      menge: zahlOderNull(z["menge"]) ?? 0,
      einheit: txt(z["einheit"]),
      einzelpreis: zahlOderNull(z["einzelpreis"]) ?? 0,
      netto: zahlOderNull(z["netto"]) ?? 0,
      art: txt(z["art"]),
      zaehlt: z["zaehlt"] !== false,
    })),
    zuschlaegeNetto: zahlOderNull(zu["netto"]) ?? 0,
    nettoPositionen: zahlOderNull(p["netto_positionen"]) ?? 0,
    netto: zahlOderNull(p["netto"]) ?? 0,
    offen: p["offen"] === true,
    zuschlagzeilen: alsListe(zu["zeilen"]).map((z): Preiszuschlagzeile => {
      const offen = txt(z["offen"]);
      return {
        code: txt(z["code"]),
        bezeichnung: txt(z["bezeichnung"]) || txt(z["code"]),
        netto: zahlOderNull(z["netto"]),
        menge: zahlOderNull(z["menge"]),
        einzelpreis: zahlOderNull(z["einzelpreis"]) ?? 0,
        einheitKey: txt(z["einheit_key"]),
        offen: offen === "menge_folgt" || offen === "betrag_fehlt" || offen === "preis_fehlt" ? offen : "",
        positionId: txt(z["position_id"]),
        jeAuftrag: z["je_auftrag"] === true,
      };
    }),
  };
}

/* ══════════════ Der Attrappenbetrieb ══════════════

   **Seit dem 18.09.2026 stehen hier Zeilen und nicht mehr eine leere
   Liste.** Bis dahin gab `begehungen()` unter `VITE_ATTRAPPE=1` `[]` zurück
   und `begehung()` warf — wer den Prototyp durchklickt (und das ist der
   Weg, auf dem `docs/29-pruefauftrag.md` geprüft wird), sah diesen Schirm
   nie gefüllt. Die Zeilen und ihr Stand liegen in
   [`attrappe/aufnahmen.ts`](attrappe/aufnahmen.ts).

   `gleich` verzögert um einen Wimpernschlag, wie es `routen.ts` tut: Ohne
   das blitzt der Ladezustand nie auf, und man baut ihn versehentlich
   falsch. */
const gleich = <T>(wert: T, ms = 120): Promise<T> =>
  new Promise((fertig) => setTimeout(() => fertig(wert), ms));

export async function begehungen(): Promise<Begehung[]> {
  if (ATTRAPPE) return gleich(A.offeneBegehungen());
  const roh = await hole<Roh>("/aufnahmen?status=offen");
  return alsListe(roh["zeilen"]).map(alsBegehung);
}

export async function begehung(id: string): Promise<Begehung> {
  if (ATTRAPPE) {
    const b = A.eineBegehung(id);
    /* Eine Kennung, die es nicht gibt, ist ein Fehler und keine leere
       Maske — sonst steht dort ein Blatt ohne Kunde und ohne Auftrag. */
    if (!b) throw new Error(`Im Attrappenbetrieb gibt es die Begehung ${id} nicht.`);
    return gleich(b);
  }
  return alsBegehung(await hole<Roh>(`/aufnahmen/${encodeURIComponent(id)}`));
}

/**
 * Aus dem Pool an sich ziehen — oder mit `wer: null` zurücklegen.
 *
 * Derselbe Weg, den der große Schirm geht (`haken.ts`). Er steht hier noch
 * einmal, weil er hier eine andere Liste auffrischen muss: Wer am Telefon
 * nimmt, sieht die Zeile sonst weiter im Pool stehen.
 */
export async function nehmen(id: string, wer?: null): Promise<void> {
  if (ATTRAPPE) return gleich(A.begehungNehmen(id, wer));
  await sende<Roh>(
    `/aufnahmen/${encodeURIComponent(id)}/nehmen`,
    wer === undefined ? {} : { wer },
  );
}

/**
 * **Gemessen?** (Befund 41, 25.09.2026) Eine selbst angelegte Aufnahme bleibt
 * nach dem Aufmaß `offen`, bis ein Angebot daraus wird — sie ist dann aber
 * keine Fahrt mehr, die jemand machen muss. Das Datum der Messung steht in
 * `erledigtIso` (`done_at`); eine verworfene trägt dort nur ihren Tag.
 */
export function begehungGemessen(b: Pick<Begehung, "status" | "erledigtIso">): boolean {
  return b.status === "erledigt" || (b.status === "offen" && b.erledigtIso !== "");
}

/**
 * **Gemessen, und es wird kein Angebot daraus** (Befund 41) —
 * `POST /aufnahmen/:id/abschliessen`. Danach ist sie erledigt; die Zahl
 * bleibt an der Aufnahme und am Einsatzort.
 */
export async function abschliessen(id: string): Promise<void> {
  if (ATTRAPPE) return gleich(A.begehungAbschliessen(id));
  await sende<Roh>(`/aufnahmen/${encodeURIComponent(id)}/abschliessen`, {});
}

/** Die Fahrt entfällt — mit Grund. Gelöscht wird nichts (Migration 0091). */
export async function verwerfen(id: string, grund: string): Promise<void> {
  if (ATTRAPPE) return gleich(A.begehungVerwerfen(id, grund));
  await sende<Roh>(`/aufnahmen/${encodeURIComponent(id)}/verwerfen`, { grund });
}

/** Was vor Ort aufgenommen wurde. Alles außer der Menge darf fehlen. */
export type Aufnahmeergebnis = {
  menge: number;
  notiz?: string;
  /**
   * Die Bilder — **Pfad und Art** (Migration 0156). Eine bloße Zeichenkette
   * gilt weiter und heißt „sonstiges"; so schickt jeder Weg, der die Arten
   * nicht kennt.
   */
  fotos?: (string | Fotoeintrag)[];
  ansprechpartner?: string;
  zufahrt?: string;
  untergrund?: string;
  hindernisse?: string;
  /** Der Stellplatz (0156) — er geht an den Ort, wie Zufahrt und Untergrund. */
  stellplatz?: string;
  /**
   * **Der Einsatzort, wenn er nachgetragen wurde** — beim Neukunden gab es
   * die Baustelle beim Bestellen noch nicht. Erst mit ihm bekommen Zufahrt,
   * Untergrund, Hindernisse und Stellplatz einen Platz zum Stehen; ohne ihn
   * trägt nur die Aufnahme die Zahl.
   */
  ortId?: string;
  /**
   * Der festgestellte Verschmutzungsgrad (G9). `null` heißt ausdrücklich
   * „keiner" — etwas anderes als „nicht gefragt", denn dann bliebe der alte
   * Grad stehen.
   */
  kategorie?: string | null;
  /**
   * Die angekreuzten Leistungszuschläge — mit Fläche (Migration 0152/0153).
   * Sie gehen mit, sobald die Leistungsart welche hat — auch leer, denn `[]`
   * heißt „keiner" und nicht „nicht gefragt". `menge: null` heißt „die ganze
   * Fläche".
   */
  zuschlaege?: { id: string; menge: number | null }[];
  /**
   * **Wann die Leistung stattfindet** (Migration 0154). Ein Tag reicht; das
   * Ende fehlt meist und ist dann derselbe. Damit verengt die Aufnahme die
   * Kalenderwoche aus dem Angebot auf den Termin — und die Pflichtgeräte
   * werden gebucht.
   */
  ausfuehrungVon?: string;
  ausfuehrungBis?: string;
};

export async function ergebnisFesthalten(id: string, e: Aufnahmeergebnis): Promise<void> {
  if (ATTRAPPE) return gleich(A.begehungErgebnis(id, e));
  const dabei = (feld: string, wert: string | undefined) =>
    wert && wert.trim() ? { [feld]: wert.trim() } : {};
  await sende<Roh>(`/aufnahmen/${encodeURIComponent(id)}/ergebnis`, {
    menge: e.menge,
    ...dabei("notiz", e.notiz),
    ...dabei("ansprechpartner", e.ansprechpartner),
    ...dabei("zufahrt", e.zufahrt),
    ...dabei("untergrund", e.untergrund),
    ...dabei("hindernisse", e.hindernisse),
    ...dabei("stellplatz", e.stellplatz),
    ...(e.ortId ? { ort_id: e.ortId } : {}),
    ...(e.fotos ? { fotos: e.fotos } : {}),
    ...(e.kategorie === undefined ? {} : { kategorie: e.kategorie }),
    ...(e.zuschlaege ? { zuschlaege: e.zuschlaege } : {}),
    ...(e.ausfuehrungVon ? { ausfuehrung_von: e.ausfuehrungVon } : {}),
    ...(e.ausfuehrungBis ? { ausfuehrung_bis: e.ausfuehrungBis } : {}),
  });
}

/* ══════════════ Spontan anlegen — ohne Angebot ══════════════

   Luca, 21.09.2026: *„Kann ich dann nicht auch direkt vor Ort ein Aufmaß
   anlegen … und dann auch daraus das Angebot zu erzeugen für einen
   Kunden?"* — *„Trage ich da die Kunden ein, wenn es sie nicht gibt,
   innerhalb dieses Prozesses, aber das alles in einem Schritt?"*

   Der Weg hat vier Schritte und kein Büro dazwischen: Kunde (notfalls neu
   als Interessent) → Aufmaß anlegen → messen → Angebot daraus. */

/**
 * Eine Aufnahme ohne Angebot anlegen (Migration 0155).
 *
 * **Die Kundennummer wird hier zur Kennung aufgelöst** — über `GET /kunden/:nr`,
 * denselben Weg, den `angebotAnlegen` geht (`routen.ts`). Der Kunde trägt im
 * Browser nur seine Nummer; die Kennung kennt nur die API. Ein zweiter Weg
 * dafür wäre eine zweite Auflösung derselben Frage.
 *
 * `ort` ist die **Bezeichnung** des Einsatzorts, nicht seine Kennung: So
 * steht er in der Kundenakte, die die Maske ohnehin geladen hat.
 */
export async function aufnahmeSpontan(eingabe: {
  kundeNr: string;
  leistungsartId: string;
  ort?: string | undefined;
  notiz?: string | undefined;
}): Promise<Begehung> {
  const kunde = await hole<Roh>(`/kunden/${encodeURIComponent(eingabe.kundeNr)}`);
  const orte = Array.isArray(kunde["orte"]) ? (kunde["orte"] as Roh[]) : [];
  const ortId = eingabe.ort
    ? txt(orte.find((o) => txt(o["label"]) === eingabe.ort)?.["id"] ?? "")
    : "";

  return alsBegehung(await sende<Roh>("/aufnahmen", {
    kunde_id: txt(kunde["id"]),
    leistungsart_id: eingabe.leistungsartId,
    ...(ortId ? { ort_id: ortId } : {}),
    ...(eingabe.notiz?.trim() ? { notiz: eingabe.notiz.trim() } : {}),
  }));
}

/**
 * **Aus der fertigen Aufnahme ein Angebot** (Migration 0155).
 *
 * Gibt die Aufnahme zurück, wie sie danach dasteht — mit `vorgang` und
 * `vorgangNr`. Zweimal gedrückt gibt denselben Vorgang: Auf einem Telefon
 * drückt man zweimal, und zwei Angebote über dieselbe Fläche wären zwei
 * Preise für eine Sache.
 */
export async function angebotAusAufnahme(
  id: string,
  betreff?: string,
): Promise<Begehung & { vorgangNr: string }> {
  const roh = await sende<Roh>(
    `/aufnahmen/${encodeURIComponent(id)}/angebot`,
    betreff?.trim() ? { betreff: betreff.trim() } : {},
  );
  return { ...alsBegehung(roh), vorgangNr: txt(roh["vorgang_nr"]) };
}

export function useAufnahmeSpontan() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: (e: Parameters<typeof aufnahmeSpontan>[0]) => aufnahmeSpontan(e),
    onSuccess: () => {
      void k.invalidateQueries({ queryKey: schluesselBegehungen });
      void k.invalidateQueries({ queryKey: ["aufnahmen"] });
    },
  });
}

export function useAngebotAusAufnahme() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, betreff }: { id: string; betreff?: string }) =>
      angebotAusAufnahme(id, betreff),
    onSuccess: (b) => {
      void k.invalidateQueries({ queryKey: ["begehung", b.id] });
      void k.invalidateQueries({ queryKey: schluesselBegehungen });
      void k.invalidateQueries({ queryKey: ["angebote"] });
      // Die Liste am Schreibtisch (Befund 19): Die Aufnahme hängt jetzt am Vorgang.
      void k.invalidateQueries({ queryKey: ["aufnahmen"] });
    },
  });
}

/* ══════════════ Die Lage einer Leistung ══════════════

   Luca, 21.09.2026: *„Da muss die Vor-Ort-Planung hergeben, dass es gegen
   die Planung prüfen kann, ob alles in dem Zeitraum verfügbar ist. Wenn
   geblockt, soll da auch stehen, welcher Auftrag blockiert und von wann bis
   wann der geblockt ist. Wenn mehrere, dann die halt."*

   Das Gegenstück zur Verfügbarkeit einer Miete, für die andere
   Positionsart: Dort heißt die Frage „habt ihr einen Radlader", hier
   „können wir am Donnerstag eine PV-Reinigung fahren". Gerechnet wird
   drüben (`dienste/leistungsressourcen.ts`) — hier wird nur angeglichen. */

/** Was im Weg liegt — eine Belegung mit Namen, nicht nur ein roter Punkt. */
export type Sperre = {
  ressourceId: string;
  kennung: string;
  /** `fest` · `laeuft` · `option` · `wartung` · `urlaub` … */
  art: string;
  von: string;
  bis: string;
  vorgang: string;
  kunde: string;
  /** Bei einer Vormerkung: bis wann sie hält. */
  frist: string;
};

/** Ein Stück oder ein Mensch mit seiner Lage im gefragten Zeitraum. */
export type Ressourcenlage = {
  id: string;
  kennung: string;
  unter: string;
  frei: boolean;
  sperren: Sperre[];
};

export type Leistungslage = {
  lage: "geht" | "teils" | "nein";
  textKey: string;
  punkt: "gut" | "acht" | "stopp";
  von: string;
  bis: string;
  geraete: {
    zuordnungId: string;
    bezeichnung: string;
    pflicht: boolean;
    bestand: number;
    frei: number;
    stuecke: Ressourcenlage[];
  }[];
  menschen: {
    verlangt: { code: string; bezeichnung: string }[];
    bestand: number;
    frei: number;
    leute: Ressourcenlage[];
  };
  /** Alles, was im Weg liegt — die Liste fürs Blatt von unten. */
  sperren: Sperre[];
};

const alsSperre = (z: Roh): Sperre => ({
  ressourceId: txt(z["ressource_id"]),
  kennung: txt(z["kennung"]),
  art: txt(z["art"]),
  von: txt(z["von"]),
  bis: txt(z["bis"]),
  vorgang: txt(z["vorgang"]),
  kunde: txt(z["kunde"]),
  frist: txt(z["frist"]),
});

const alsRessourcenlage = (z: Roh): Ressourcenlage => ({
  id: txt(z["id"]),
  kennung: txt(z["kennung"]),
  unter: txt(z["unter"]),
  frei: z["frei"] === true,
  sperren: alsListe(z["sperren"]).map(alsSperre),
});

export async function leistungslage(
  leistung: string,
  von: string,
  bis: string,
): Promise<Leistungslage> {
  const teile = new URLSearchParams({ leistung, von, bis });
  const z = await hole<Roh>(`/leistungslage?${teile.toString()}`);
  const m = (typeof z["menschen"] === "object" && z["menschen"] !== null
    ? z["menschen"] as Roh : {});
  return {
    lage: (txt(z["lage"]) || "nein") as Leistungslage["lage"],
    textKey: txt(z["text_key"]),
    punkt: (txt(z["punkt"]) || "acht") as Leistungslage["punkt"],
    von: txt(z["von"]),
    bis: txt(z["bis"]),
    geraete: alsListe(z["geraete"]).map((g) => ({
      zuordnungId: txt(g["zuordnung_id"]),
      bezeichnung: txt(g["bezeichnung"]),
      pflicht: g["pflicht"] === true,
      bestand: zahlOderNull(g["bestand"]) ?? 0,
      frei: zahlOderNull(g["frei"]) ?? 0,
      stuecke: alsListe(g["stuecke"]).map(alsRessourcenlage),
    })),
    menschen: {
      verlangt: alsListe(m["verlangt"]).map((q) => ({
        code: txt(q["code"]), bezeichnung: txt(q["label"]) || txt(q["code"]),
      })),
      bestand: zahlOderNull(m["bestand"]) ?? 0,
      frei: zahlOderNull(m["frei"]) ?? 0,
      leute: alsListe(m["leute"]).map(alsRessourcenlage),
    },
    sperren: alsListe(z["sperren"]).map(alsSperre),
  };
}

/**
 * Die Lage einer Leistung im gewählten Zeitraum.
 *
 * **Im Attrappenbetrieb wird nicht gefragt** — eine erfundene Zusage wäre
 * schlimmer als kein Abschnitt (dieselbe Linie wie bei `nachfassungen`).
 */
export function useLeistungslage(leistung: string, von: string, bis: string) {
  return useQuery({
    queryKey: ["leistungslage", leistung, von, bis] as const,
    queryFn: () => leistungslage(leistung, von, bis),
    enabled: !ATTRAPPE && Boolean(leistung && von && bis),
    placeholderData: (vorher: Leistungslage | undefined) => vorher,
  });
}

/* ══════════════ Nachfassen ══════════════ */

/** Ein offenes Angebot, wie es am Telefon steht — Name, Nummer, Betrag, Frist. */
export type Nachfassung = {
  id: string;
  nummer: string;
  betreff: string;
  kunde: string;
  kundennummer: string;
  /** Die Nummer, die der Wählknopf nimmt. Leer heißt: keine hinterlegt. */
  telefon: string;
  ansprechpartner: string;
  ort: string;
  ortschaft: string;
  netto: number;
  /** Die Bindefrist — sie steht auf dem Papier beim Kunden. */
  fristIso: string;
  /** Die Wiedervorlage — sie steht nur hier. */
  wiedervorlageIso: string;
  wiedervorlageVon: string;
  /** Tage bis zum früheren der beiden. Negativ heißt: war schon. Ordnet die Liste. */
  tage: number | null;
  /** Tage bis zur Bindefrist — die Zahl neben der Frist (AG-35). */
  tageFrist: number | null;
  /** Tage bis zur Wiedervorlage — die Zahl neben der Wiedervorlage. */
  tageWiedervorlage: number | null;
};

function alsNachfassung(z: Roh): Nachfassung {
  return {
    id: txt(z["id"]),
    nummer: txt(z["number"]),
    betreff: txt(z["subject"]),
    kunde: txt(z["kunde"]),
    kundennummer: txt(z["kundennummer"]),
    telefon: txt(z["telefon"]),
    ansprechpartner: txt(z["ansprechpartner"]),
    ort: txt(z["ort"]),
    ortschaft: txt(z["ortschaft"]),
    netto: zahlOderNull(z["netto"]) ?? 0,
    fristIso: txt(z["valid_until"]),
    wiedervorlageIso: txt(z["follow_up_on"]),
    wiedervorlageVon: txt(z["wiedervorlage_von"]),
    tage: zahlOderNull(z["tage"]),
    tageFrist: zahlOderNull(z["tage_frist"]),
    tageWiedervorlage: zahlOderNull(z["tage_wiedervorlage"]),
  };
}

/**
 * **Im Attrappenbetrieb bleibt diese Liste leer, und das ist Absicht.**
 *
 * Der Auszug „Nachfassen" auf `/aufnahmen` steht (er kommt aus
 * `verkaufsmappe.ts` und trägt keinen Betrag). Dieser Schirm dagegen zeigt
 * je Zeile das **Netto des Angebots** — und `Nachfassung.netto` ist eine
 * Zahl ohne `null`. Eine `0` sähe aus wie ein Nullpreis, und der Betrieb hat
 * keine gewachsene Preisliste (CLAUDE.md, „Was ohne Rückfrage nicht gesetzt
 * werden darf"; `routen.ts`: *„Beträge, die es nicht gibt, bleiben `null`"*).
 *
 * Wer den Schirm im Prototyp sehen will, braucht also entweder einen
 * Betrag, den jemand beschlossen hat, oder ein `netto`, das fehlen darf.
 * Beides ist eine Entscheidung und keine Attrappenzeile.
 */
export async function nachfassungen(): Promise<Nachfassung[]> {
  if (ATTRAPPE) return [];
  return alsListe((await hole<Roh>("/nachfassen"))["zeilen"]).map(alsNachfassung);
}

/** Die Stufen, die der Knopf anbietet. Dieselben drei wie in der Route. */
export const STUFEN = [3, 7, 14] as const;
export type Stufe = (typeof STUFEN)[number];

export async function wiedervorlegen(id: string, tage: Stufe): Promise<void> {
  if (ATTRAPPE) return;
  await sende<Roh>(`/nachfassen/${encodeURIComponent(id)}/wiedervorlage`, { tage });
}

/* ══════════════ Die Haken ══════════════ */

export const schluesselBegehungen = ["begehungen"] as const;
export const schluesselNachfassen = ["nachfassen"] as const;

/**
 * `aktiv` ist nicht Zierde: Auf dem Startschirm steht die Kachel nur für den
 * Außendienst kaufmännisch, und ein Fahrer auf Baustellenfunk soll nicht zwei
 * Listen holen, die er nie öffnet. Haken lassen sich nicht bedingt aufrufen —
 * die Abfrage schon.
 */
export const useBegehungen = (aktiv = true) =>
  useQuery({ queryKey: schluesselBegehungen, queryFn: begehungen, enabled: aktiv });

export const useBegehung = (id: string) =>
  useQuery({ queryKey: ["begehung", id], queryFn: () => begehung(id), enabled: !!id });

/**
 * Nach einem Ergebnis ist mehr fällig als die eigene Liste: Die Menge steht
 * jetzt an der Position, der Pool am großen Schirm ist kürzer, und unter
 * „Was ist zu tun?" fällt eine Zeile weg. Wer nur seine Liste auffrischt,
 * sieht die Aufnahme dort weiterleben.
 */
function danach(k: ReturnType<typeof useQueryClient>, id?: string) {
  void k.invalidateQueries({ queryKey: schluesselBegehungen });
  if (id) void k.invalidateQueries({ queryKey: ["begehung", id] });
  void k.invalidateQueries({ queryKey: ["aufnahmen"] });
  void k.invalidateQueries({ queryKey: ["vorgang"] });
  void k.invalidateQueries({ queryKey: ["positionen"] });
  void k.invalidateQueries({ queryKey: ["start"] });
  void k.invalidateQueries({ queryKey: ["handy"] });
}

export function useErgebnisFesthalten() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...e }: Aufnahmeergebnis & { id: string }) => ergebnisFesthalten(id, e),
    onSuccess: (_leer, e) => danach(k, e.id),
  });
}

export function useBegehungNehmen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, wer }: { id: string; wer?: null }) => nehmen(id, wer),
    onSuccess: (_leer, e) => danach(k, e.id),
  });
}

export function useBegehungAbschliessen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => abschliessen(id),
    onSuccess: (_leer, e) => danach(k, e.id),
  });
}

export function useBegehungVerwerfen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, grund }: { id: string; grund: string }) => verwerfen(id, grund),
    onSuccess: (_leer, e) => danach(k, e.id),
  });
}

export function useNachfassungen(aktiv = true) {
  return useQuery({ queryKey: schluesselNachfassen, queryFn: nachfassungen, enabled: aktiv });
}

export function useWiedervorlegen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tage }: { id: string; tage: Stufe }) => wiedervorlegen(id, tage),
    onSuccess: () => {
      void k.invalidateQueries({ queryKey: schluesselNachfassen });
      void k.invalidateQueries({ queryKey: ["vorgang"] });
    },
  });
}

/* ══════════════ Tage und Zahlen ══════════════ */

/** `16.09.` aus einem Kalendertag `JJJJ-MM-TT` oder einem Zeitpunkt. */
export function tagKurz(wert: string): string {
  return tagKurzImBetrieb(wert); // ein Zeitpunkt zählt mit seinem deutschen Tag (N8-02)
}

/** Wie viele Tage ein Zeitpunkt zurückliegt — für „seit 4 Tagen offen". */
export function tageSeit(iso: string): number | null {
  const bis = tageBis(iso); // deutsche Kalendertage (N8-02)
  return bis === null ? null : -bis;
}

/**
 * Die Dringlichkeit einer Nachfassung — drei Stufen, nicht fünf.
 *
 * `stopp` ist abgelaufen, `acht` ist heute oder in den nächsten drei Tagen,
 * sonst nichts. Die Zahl steht daneben; die Farbe trägt die Aussage nie
 * allein (28-designkonzept.md).
 */
export function dringlichkeit(tage: number | null): "stopp" | "acht" | "" {
  if (tage === null) return "";
  if (tage < 0) return "stopp";
  return tage <= 3 ? "acht" : "";
}
